/**
 * useTablePrefs — Persists the World-DB table's view preferences
 * (sort, status/import filter, flow filter, advanced filters, search, …)
 * so they survive a page refresh or tab switch.
 *
 * Stored in localStorage, namespaced per userId, with a 30-minute TTL.
 * After 30 minutes of inactivity the cache is dropped and the table falls
 * back to its defaults. Every save refreshes the TTL, so an actively used
 * table keeps its state.
 */
import { useCallback, useState } from "react";

const PREFS_TTL_MS = 30 * 60 * 1000; // 30 Minuten
const PREFS_STORAGE_PREFIX = "worldDbTablePrefs";

const buildPrefsKey = (userId) => `${PREFS_STORAGE_PREFIX}:${userId || "anon"}`;

export const readTablePrefs = (userId) => {
  try {
    const raw = window.localStorage.getItem(buildPrefsKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const isFresh =
      typeof parsed.savedAt === "number" &&
      Date.now() - parsed.savedAt <= PREFS_TTL_MS;
    if (!isFresh) {
      window.localStorage.removeItem(buildPrefsKey(userId));
      return null;
    }
    return parsed.values && typeof parsed.values === "object"
      ? parsed.values
      : null;
  } catch (_) {
    return null;
  }
};

export const writeTablePrefs = (userId, values) => {
  try {
    window.localStorage.setItem(
      buildPrefsKey(userId),
      JSON.stringify({ savedAt: Date.now(), values })
    );
  } catch (_) {
    // localStorage nicht verfügbar oder Quota voll — Persistenz einfach überspringen.
  }
};

export const clearTablePrefs = (userId) => {
  try {
    window.localStorage.removeItem(buildPrefsKey(userId));
  } catch (_) {
    // ignorieren
  }
};

/**
 * Returns the restored prefs (read once on mount) and a stable `persist`
 * function to write the current view state back to the cache.
 */
export const useTablePrefs = (userId) => {
  const [initialPrefs] = useState(() => readTablePrefs(userId) || {});
  const persist = useCallback(
    (values) => writeTablePrefs(userId, values),
    [userId]
  );
  return { initialPrefs, persist };
};
